import { useEffect, useState } from 'react';

import { fetchUserRoleGroupReason, revokeRoleGroupFromUser, useFetchUserRoleGroupAssignments } from '@jobandtalent/auth-api-client-v2';
import { Button } from '@jobandtalent/design-system';

import Privilege from 'src/components/Privilege/Privilege';
import Section from 'src/components/Section/Section';
import useError, { DEFAULT_ERROR } from 'src/hooks/useError/useError';
import useLocalPermissions from 'src/hooks/useLocalPermissions/useLocalPermissions';
import useNotification, { DEFAULT_SUCCESS } from 'src/hooks/useNotification/useNotification';

import NewRoleGroupForm from '../NewRoleGroupForm/NewRoleGroupForm';
import { UserRoleGroupAssignment } from '../Users.types';
import { useUserState } from '../UserState';

import styles from './EditUserForm.module.scss';

const UserRoleGroupsSection = () => {
  const { state } = useUserState();
  const userId = state.user?.id;
  const { error, setError } = useError();
  const { notification, setNotification } = useNotification();
  const { permissions, error: permissionError } = useLocalPermissions();
  const [showNewRoleGroupForm, setShowNewRoleGroupForm] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedRoleGroup, setSelectedRoleGroup] = useState<UserRoleGroupAssignment | null>(null);
  const {
    data: roleGroupsData,
    error: roleGroupsError,
    mutate: roleGroupsMutate
  } = useFetchUserRoleGroupAssignments(state.user?.id as number);

  useEffect(() => {
    if (roleGroupsError) {
      setError(roleGroupsError?.response?.data ? roleGroupsError.response.data : DEFAULT_ERROR);
    }
  }, [roleGroupsError]);

  const serviceUser = state.user?.service_user;
  const serviceUsersPermission = !permissionError && permissions.serviceUsersPermission;
  const canManageRoleGroups = !serviceUser || serviceUsersPermission;

  const onClickRevokeRoleGroup = async (roleGroupId: number) => {
    if (!state.user) {
      return;
    }

    revokeRoleGroupFromUser(state.user.id, roleGroupId).then(
      () => roleGroupsMutate(),
      (requestError) => {
        setError(requestError?.response?.data ? requestError.response.data : DEFAULT_ERROR);
      }
    );
  };

  const onCloseRoleGroupForm = () => {
    setBulkMode(false);
    setShowNewRoleGroupForm(false);
    setSelectedRoleGroup(null);
  };

  const onSuccessNewRoleGroupForm = () => {
    roleGroupsMutate();
    setNotification(DEFAULT_SUCCESS);
    onCloseRoleGroupForm();
  };

  return (
    <>
      <Section icon="person_accepted" title="Roles Groups">
        {roleGroupsData?.data.user_role_groups?.map((roleGroup) => (
          <Privilege
            key={roleGroup.id}
            title={roleGroup.role_group_name}
            canReadReason={!permissionError && permissions.readAssignmentDetailsPermission}
            loadReason={userId !== undefined ? () => fetchUserRoleGroupReason(userId, roleGroup.role_group_id) : undefined}
            onEdit={canManageRoleGroups ? () => {
              setBulkMode(false);
              setSelectedRoleGroup(roleGroup);
              setShowNewRoleGroupForm(true);
            } : undefined}
            onRevoke={canManageRoleGroups ? () => onClickRevokeRoleGroup(roleGroup.role_group_id) : undefined}
          />
        ))}
        <div className={styles.grantActions}>
          <Button
            className={styles.grantPrivilege}
            dataAttributes={{ testid: 'grantRoleGroupButton' }}
            disabled={!canManageRoleGroups}
            onSelect={() => {
              setBulkMode(false);
              setSelectedRoleGroup(null);
              setShowNewRoleGroupForm(true);
            }}
            secondary
            tiny
          >
            Grant role group
          </Button>
          <Button
            disabled={!canManageRoleGroups}
            className={styles.grantPrivilege}
            onSelect={() => {
              setBulkMode(true);
              setShowNewRoleGroupForm(true);
            }}
            secondary
            tiny
          >
            Bulk add
          </Button>
        </div>
      </Section>
      {showNewRoleGroupForm && state.user && (
        <NewRoleGroupForm
          bulk={bulkMode}
          onAssignmentsChanged={() => roleGroupsMutate()}
          onClose={onCloseRoleGroupForm}
          onSuccess={onSuccessNewRoleGroupForm}
          selectedUser={state.user}
          selectedRoleGroup={selectedRoleGroup}
        />
      )}
      {error}
      {notification}
    </>
  );
};

export default UserRoleGroupsSection;
